import { Editor } from './editor';
import { getPageMetaData } from '../utils/get-page-meta-data';
import { trackAPIMethods } from '../tracking';
import {
	INSERT,
	MODIFY_CTA,
	RESTRICTIVELY_MODIFY_CTA,
	MODIFY_LINKS,
	UPDATE
} from './constants';
import { allowSelectedUpdatesToButtonIntents } from './locations';
import { watchForLateLinks } from './link-watcher';
import { calculateTargetListingLocation } from './listingLocationInsertion';
import { getConfig } from '../utils/load-configs';
import { waitForElement, waitForMenu } from '../utils/wait-for';
import { log } from '../log';
import '../../types/edit.d';

// Mobile menu override to ensure callbacks are executed for replaced links
const addMobileMenuOverride = (intent, callback) => {
	// Remove any existing override for this intent
	const existingOverride = document.querySelector(
		`[data-mobile-override="${intent}"]`
	);
	if (existingOverride) {
		existingOverride.remove();
	}

	// Create a global click handler for mobile menu links
	const clickHandler = event => {
		const target = event.target.closest('a');
		if (!target) return;

		const href = target.getAttribute('href');
		if (!href) return;

		// Check if this link is in a mobile menu
		const isInMobileMenu =
			target.closest('.ddc-mobile-header-nav') ||
			target.closest('[data-location~="primary-menu"]') ||
			target.closest('.mobile-menu') ||
			target.closest('.ddc-mobile-header-sub-nav') ||
			target.closest('#ddc-mobile-header-nav');

		// Check if link has been processed by our integration AND matches this specific intent
		// This prevents race conditions when multiple updateLink calls are made
		const linkIntent = target.dataset.wiapiIntent;
		const shouldIntercept =
			isInMobileMenu && target.dataset.wiapiProcessed && linkIntent === intent;

		// prevent default page link in case updateLink fails
		if (shouldIntercept) {
			event.preventDefault();
			event.stopPropagation();

			// Execute the integration callback
			try {
				const result = callback();

				// If the callback returns an object with an onclick function, execute it
				if (
					result &&
					typeof result === 'object' &&
					typeof result.onclick === 'function'
				) {
					result.onclick();
				}
			} catch (error) {
				log('[MOBILE_OVERRIDE] Error executing callback:', error);
			}
		}
	};

	// Add the event listener with capture to catch it early
	document.addEventListener('click', clickHandler, { capture: true });

	// Mark that we've added this override
	const marker = document.createElement('div');
	marker.setAttribute('data-mobile-override', intent);
	marker.style.display = 'none';
	document.body.appendChild(marker);

	log('[MOBILE_OVERRIDE] Added mobile menu override for intent:', intent);
};

const standardModifiers = new Set();
const vehicleModifiers = new Set();
export const transientVehicleModifiers = new Set();

/* MODIFY_LINKS editors resolve their target from the WISE schema and search the
 DOM for `a[href^="..."]`. If the links are not on the page yet, the editor
 finds nothing and - unlike inserts and CTA updates - was never retried,
 because MODIFY_LINKS is not one of the methodTypes registered as a standard
 modifier below. Widgets that render links after an async fetch (ws-specials)
 therefore lose the takeover on any load where their fetch is slow (DE749901).
 Keep these editors so they can be re-applied when late links are announced. */
export const linkModifiers = new Set();

/**
 * For each stored Editor with the StandardModifier label,
 * Check if the Editor should be invoked with `Editor.apply()`.
 *
 * @param { InvokeStandardInsertsParams? } settings
 */
export const invokeStandardInserts = (settings = {}) => {
	standardModifiers.forEach(insert => {
		const { exclude, include } = settings;

		if (!insert.targetLocation) {
			log('No target location was set for insert:', insert);
			return;
		}

		// Skip invokation if the targetLocation is excluded
		if (exclude && insert.targetLocation.startsWith(exclude)) {
			return;
		}

		// Skip invokation if the targetLocation is not included
		if (include && !insert.targetLocation.startsWith(include)) {
			return;
		}

		insert.apply();
	});
};

/**
 * For each stored Editor with the StandardModifier label,
 * invoke with `Editor.apply()`.
 */
export const invokeStandardUpdates = () => {
	standardModifiers.forEach(update => {
		update.apply();
	});
};

/**
 * Re-apply every stored MODIFY_LINKS Editor.
 *
 * Safe to call repeatedly: `Editor.apply()` skips locations it has already
 * modified via its `data-web-api-unique-id` check.
 */
export const invokeLinkModifiers = () => {
	linkModifiers.forEach(modifier => {
		modifier.apply();
	});
};

/**
 * For each stored Editor with the transientVehicleModifiers or vehicleModifiers label,
 * invoke with `Editor.apply()` with the `vehicleData` and a `true` `isVehicleEvent`
 *
 * @param { InvokeVehicleInsertsAndUpdatesParams } vehicleData
 */
export const invokeVehicleInsertsAndUpdates = vehicleData => {
	transientVehicleModifiers.forEach(insert => {
		insert.apply(vehicleData, true);
	});
	vehicleModifiers.forEach(insert => {
		insert.apply(vehicleData, true);
	});
};

const edit = (
	methodName,
	methodType,
	init,
	location,
	type,
	intent,
	callback,
	subscribe = true
) => {
	const editor = new Editor(
		init,
		methodName,
		methodType,
		location,
		type,
		intent,
		callback
	);

	const isNewSrpVehicleLocation =
		window.DDC.InvData && location && location.startsWith('vehicle');

	if (subscribe) {
		if (
			methodType === INSERT ||
			[UPDATE, MODIFY_CTA, RESTRICTIVELY_MODIFY_CTA].includes(methodType)
		) {
			if (location && location.startsWith('vehicle')) {
				vehicleModifiers.add(editor);
			} else {
				standardModifiers.add(editor);
			}
		}
	} else if (!subscribe && isNewSrpVehicleLocation) {
		transientVehicleModifiers.add(editor);
	}

	// Tracked regardless of `subscribe`: updateLink defaults it to false, and a
	// link takeover always needs to survive links that arrive after this pass.
	if (methodType === MODIFY_LINKS) {
		linkModifiers.add(editor);

		/* Installed here rather than at startup so that pages with no link
		 takeover configured never create an observer. Covers any widget that
		 renders links late, without needing the widget to announce itself. */
		watchForLateLinks(invokeLinkModifiers);
	}

	editor.apply();

	const locationName = intent || location;
	trackAPIMethods(init, { methodType, locationName });

	return editor;
};

export const insert = async (init, location, callback, subscribe = true) => {
	if (typeof init !== 'object' || init === null) {
		throw new Error(`init must be an object. Got: ${init}.`);
	}

	const targetLocation = await calculateTargetListingLocation(init, location);

	return edit(
		'insert',
		INSERT,
		init,
		targetLocation,
		null,
		null,
		callback,
		subscribe
	);
};

export const insertOnce = async (init, location, callback) => {
	return insert(init, location, callback, false);
};

export const insertCallToAction = async (
	init,
	type,
	intent,
	callback,
	subscribe = true
) => {
	let location = null;
	let ctaIntent;

	const config = await getConfig(init);

	if (type === 'button') {
		location = config?.useCustomInsertLocations
			? 'vehicle-ctas-custom'
			: 'vehicle-ctas';
		ctaIntent = `vehicle-${intent}-button`;
	}

	const methodType = allowSelectedUpdatesToButtonIntents.includes(intent)
		? RESTRICTIVELY_MODIFY_CTA
		: MODIFY_CTA;

	return edit(
		'insertCallToAction',
		methodType,
		init,
		location,
		type,
		ctaIntent,
		callback,
		subscribe
	);
};

export const insertCallToActionOnce = async (init, type, intent, callback) => {
	return insertCallToAction(init, type, intent, callback, false);
};

export const update = async (init, location, callback, subscribe = true) => {
	return edit(
		'update',
		UPDATE,
		init,
		location,
		null,
		null,
		callback,
		subscribe
	);
};

export const updateOnce = async (init, location, callback) => {
	return update(init, location, callback, false);
};

// Helper function to wait for fragment navigation content to load
const waitForFragmentContent = async (intent, init) => {
	// Early return if no hamburger menu with fragment navigation exists
	const navFragHamburgerMenu = document.querySelector(
		'a[data-href*="/navigation-fragments/"], a[data-href*="/fragments/"]'
	);
	if (!navFragHamburgerMenu) {
		log(
			'[FRAGMENT_WAIT] No hamburger menu with fragment navigation found, skipping wait'
		);
		return;
	}

	const isLegacyMenu = !!document.querySelector(
		'[data-location~="primary-menu"]'
	);
	let fragmentNav;
	if (isLegacyMenu) {
		fragmentNav = await waitForElement('.fragment-content');
	} else {
		fragmentNav = await waitForElement('.ddc-mobile-fragment-nav');
	}

	// Get the content mappings to determine which links to look for
	let linkTargets = [];
	try {
		const { getContentMappings } = await import('../utils/load-configs');
		const linkMappings = (await getContentMappings(init)).links || {};
		linkTargets = linkMappings[intent] || [];
	} catch (error) {
		log('[FRAGMENT_WAIT] Error getting content mappings:', error);
		// Fallback: just wait for any links to appear
	}

	const checkForContent = () => {
		// If we have specific link targets from content mappings, use those
		if (linkTargets.length > 0) {
			const searchSelectors = linkTargets.map(target => `a[href^="${target}"]`);
			const hasTargetLinks = searchSelectors.some(
				selector => fragmentNav.querySelector(selector) !== null
			);

			if (hasTargetLinks) {
				return true;
			}
		} else {
			// Fallback: just check if the fragment has any links
			const hasAnyLinks = fragmentNav.querySelector('a') !== null;
			if (hasAnyLinks) {
				return true;
			}
		}

		return false;
	};

	// Check if content is already present
	if (checkForContent()) {
		return;
	}

	// Wait for content to be loaded via mutation observer
	await new Promise(resolve => {
		const observer = new MutationObserver(() => {
			if (checkForContent()) {
				observer.disconnect();
				resolve();
			}
		});

		// Watch the fragment nav container for changes
		observer.observe(fragmentNav, {
			childList: true,
			subtree: true,
			attributes: false
		});

		// Clean resolve after observer is set up - if for some reason
		// the content appears between initial check and observer setup
		if (checkForContent()) {
			observer.disconnect();
			resolve();
		}
	});
};

export const updateLink = async (init, intent, callback, subscribe = false) => {
	const doEdit = () => {
		return edit(
			'updateLink',
			MODIFY_LINKS,
			init,
			null,
			'links',
			intent,
			callback,
			subscribe
		);
	};

	// Apply link updates immediately
	const editor = doEdit();

	// Check if we're in mobile layout and need to wait for menu, then apply again
	try {
		const data = await getPageMetaData();
		const isOnMobile = data.layoutType === 'mobile';

		if (isOnMobile) {
			// Wait for mobile menu to be ready before applying changes again
			await waitForMenu();

			// For fragment navigation, wait for the actual content to load
			// if not fragment navigation, this will be a no-op
			await waitForFragmentContent(intent, init, isOnMobile);

			// Apply the changes again after menu content is ready
			doEdit();

			// Add mobile menu override to handle clicks on dynamic mobile menu links
			addMobileMenuOverride(intent, callback);
		}
	} catch (error) {
		// If metadata check fails, proceed with normal flow
		log(
			'Could not determine layout type, proceeding with standard link update:',
			error
		);
	}

	return editor;
};
